import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/auth';
import { useLogout } from '../features/auth/useAuth';
import { Modal } from './ui/Modal';
import { QuickAddTransactionForm } from '../features/transactions/QuickAddTransaction';
import { TransferForm } from '../features/transfers/TransferForm';
import { PlannedForm } from '../features/planned/PlannedForm';
import { NotificationCenter } from './ui/NotificationCenter';
import { fetchMe } from '../features/auth/api';
import { useAssistantVisualViewport } from '../hooks/useAssistantVisualViewport';
import { NavIcon, type NavIconName } from './ui/NavIcon';
import { useAccounts } from '../features/accounts/useAccounts';
import { Tooltip } from './ui/Tooltip';

interface NavItem {
  to: string;
  label: string;
  icon: NavIconName;
  badge?: string;
  ariaLabel?: string;
}

/** Tabs primarios (barra inferior móvil). */
const NAV_PRIMARY: NavItem[] = [
  { to: '/panorama', label: 'Panorama', icon: 'chart' },
  { to: '/cuentas', label: 'Cuentas', icon: 'bank' },
  {
    to: '/asistente',
    label: 'Asistente',
    icon: 'spark',
    badge: 'PLUS',
    ariaLabel: 'Asistente Plus',
  },
  { to: '/transacciones', label: 'Movimientos', icon: 'swap' },
  { to: '/mas', label: 'Más', icon: 'more' },
];

const MORE_ROUTES = [
  '/mas',
  '/tarjeta',
  '/gastos-fijos',
  '/deudas',
  '/presupuesto',
  '/metas',
  '/ajustes',
  '/admin',
];

function mobileNavItemIsActive(item: NavItem, pathname: string): boolean {
  const routes = item.to === '/mas' ? MORE_ROUTES : [item.to];
  return routes.some((route) => pathname === route || pathname.startsWith(`${route}/`));
}

/** Navegación completa (sidebar escritorio). */
const NAV_SIDEBAR: NavItem[] = [
  { to: '/panorama', label: 'Panorama', icon: 'chart' },
  { to: '/asistente', label: 'Asistente', icon: 'spark' },
  { to: '/cuentas', label: 'Cuentas', icon: 'bank' },
  { to: '/transacciones', label: 'Movimientos', icon: 'swap' },
  { to: '/tarjeta', label: 'Tarjeta', icon: 'card' },
  { to: '/gastos-fijos', label: 'Movimientos fijos', icon: 'calendar' },
  { to: '/deudas', label: 'Deudas', icon: 'wallet' },
  { to: '/presupuesto', label: 'Presupuesto', icon: 'filter' },
  { to: '/metas', label: 'Metas', icon: 'target' },
  { to: '/ajustes', label: 'Ajustes', icon: 'gear' },
];

export function AppLayout() {
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const logout = useLogout();
  const location = useLocation();
  const navigate = useNavigate();
  const isAssistantRoute = location.pathname === '/asistente';
  const appShellRef = useRef<HTMLDivElement>(null);
  const mobileNavRef = useRef<HTMLElement>(null);
  const assistantKeyboardOpen = useAssistantVisualViewport(isAssistantRoute, appShellRef);
  const [quickActionsOpen, setQuickActionsOpen] = useState(false);
  const [quickFlow, setQuickFlow] = useState<'transaction' | 'transfer' | 'planned' | null>(null);
  const accounts = useAccounts();
  // Refrescamos el perfil al montar: una promoción de rol se ve al recargar sin
  // convertir el navegador en la fuente de autorización (la API lo verifica).
  const profile = useQuery({ queryKey: ['profile'], queryFn: fetchMe, staleTime: 5 * 60_000 });
  useEffect(() => {
    if (!profile.data) return;
    setUser({
      id: profile.data.id,
      email: profile.data.email,
      name: profile.data.name,
      role: profile.data.role,
      defaultCurrency: profile.data.defaultCurrency,
      aiConsentGranted: profile.data.aiConsentGranted,
    });
  }, [profile.data, setUser]);
  useEffect(() => {
    if (!isAssistantRoute || assistantKeyboardOpen) return;
    const shell = appShellRef.current;
    const navigation = mobileNavRef.current;
    if (!shell || !navigation) return;

    const publishNavigationHeight = () => {
      shell.style.setProperty(
        '--mobile-nav-height',
        `${Math.ceil(navigation.getBoundingClientRect().height)}px`,
      );
    };
    publishNavigationHeight();

    const observer = new ResizeObserver(publishNavigationHeight);
    observer.observe(navigation);
    window.addEventListener('resize', publishNavigationHeight);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', publishNavigationHeight);
    };
  }, [assistantKeyboardOpen, isAssistantRoute]);
  const sidebarItems =
    user?.role === 'admin'
      ? [...NAV_SIDEBAR, { to: '/admin/ia', label: 'Administración', icon: 'gear' as const }]
      : NAV_SIDEBAR;
  const canQuickAdd = accounts.isSuccess && (accounts.data?.length ?? 0) > 0;

  const openQuickFlow = (flow: Exclude<typeof quickFlow, null>) => {
    setQuickActionsOpen(false);
    setQuickFlow(flow);
  };

  const openCardCharge = () => {
    setQuickActionsOpen(false);
    // La tarjeta tiene reglas distintas a un movimiento bancario. Abrir su vista
    // evita registrar una compra de tarjeta como egreso y contarla dos veces.
    navigate('/tarjeta?accion=nuevo-cargo');
  };

  const openPending = () => {
    setQuickActionsOpen(false);
    navigate('/transacciones?mostrar=pendientes');
  };

  return (
    <div
      ref={appShellRef}
      data-assistant-keyboard={assistantKeyboardOpen ? 'open' : 'closed'}
      className={`${isAssistantRoute ? 'assistant-app-shell' : 'min-h-full'} bg-slate-50 lg:h-[100dvh] lg:overflow-hidden`}
    >
      <div className={`${isAssistantRoute ? 'h-full min-h-0' : 'min-h-full'} lg:flex lg:h-full`}>
        {/* Sidebar (escritorio) */}
        <aside className="hidden lg:flex lg:w-[248px] lg:shrink-0 lg:flex-col lg:overflow-y-auto lg:border-r lg:border-slate-200 lg:bg-white lg:px-[14px] lg:py-[22px]">
          <div className="flex items-center gap-2.5 px-2 pb-[22px] text-[15px] font-bold tracking-tight text-slate-900">
            <span className="flex h-[34px] w-[34px] items-center justify-center rounded-[11px] bg-slate-900 text-white">
              <NavIcon name="chart" className="h-[18px] w-[18px]" />
            </span>
            Asistente Financiero
          </div>
          <nav className="flex flex-1 flex-col gap-[3px]">
            {sidebarItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/panorama'}
                className={({ isActive }) =>
                  `tap-target flex items-center gap-[11px] rounded-xl px-3 text-sm font-medium ${
                    isActive
                      ? 'bg-slate-900 text-white'
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                  }`
                }
              >
                <NavIcon name={item.icon} className="h-[19px] w-[19px]" />
                {item.label}
                {item.to === '/asistente' && (
                  <span className="ml-auto rounded-md bg-slate-800 px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-emerald-300">
                    PLUS
                  </span>
                )}
              </NavLink>
            ))}
          </nav>
          <div className="mt-auto flex items-center gap-2.5 px-2 pt-5">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-[13px] font-bold text-white">
              {(user?.name ?? 'U')
                .split(' ')
                .map((word) => word[0])
                .slice(0, 2)
                .join('')
                .toUpperCase()}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[13px] font-semibold text-slate-900">
                {user?.name ?? 'Usuario'}
              </span>
              <span className="block truncate text-[11px] text-slate-500">{user?.email}</span>
            </span>
            <button
              onClick={() => logout.mutate()}
              className="text-xs text-slate-500 hover:text-expense"
              aria-label="Cerrar sesión"
            >
              Salir
            </button>
          </div>
        </aside>

        {/* Contenido */}
        <div
          className={`flex flex-1 flex-col ${isAssistantRoute ? 'h-full min-h-0 overflow-hidden' : 'min-h-full'} lg:min-h-0 lg:overflow-y-auto`}
        >
          <main
            className={
              isAssistantRoute
                ? 'assistant-viewport min-h-0 flex-none overflow-hidden px-0 pb-0 pt-0 lg:h-full lg:flex-1'
                : 'flex-1 px-5 pt-[calc(16px+env(safe-area-inset-top,0px))] lg:min-h-0 lg:px-7 lg:pt-6'
            }
          >
            {isAssistantRoute ? (
              <Outlet />
            ) : (
              <div
                data-testid="app-page-content"
                className="min-h-full pb-[calc(10.5rem+env(safe-area-inset-bottom,0px))] lg:pb-28"
              >
                <Outlet />
              </div>
            )}
          </main>

          {/* Tab bar (móvil) */}
          {(!isAssistantRoute || !assistantKeyboardOpen) && (
            <nav
              ref={mobileNavRef}
              aria-label="Navegación principal"
              className="fixed inset-x-0 bottom-0 z-10 flex justify-around border-t border-slate-200 bg-white/95 px-1 pt-2 shadow-[0_-6px_24px_-18px_rgba(15,23,42,.25)] backdrop-blur-lg pb-safe lg:hidden"
            >
              {NAV_PRIMARY.map((item) => {
                const active = mobileNavItemIsActive(item, location.pathname);
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    aria-label={item.ariaLabel}
                    aria-current={active ? 'page' : undefined}
                    className={`tap-target flex flex-1 flex-col items-center justify-center gap-[3px] py-1 text-xs font-medium ${
                      active ? 'text-slate-900' : 'text-slate-600'
                    }`}
                  >
                    <span className="relative" aria-hidden>
                      <NavIcon name={item.icon} className="h-[23px] w-[23px]" />
                      {item.badge && (
                        <span className="absolute -right-3 -top-2 rounded-full bg-emerald-100 px-1 text-[7px] font-black leading-3 tracking-wide text-emerald-800">
                          {item.badge}
                        </span>
                      )}
                    </span>
                    {item.label}
                  </Link>
                );
              })}
            </nav>
          )}
        </div>

        {/* Movimientos ya tiene accesos visibles para registrar, traspasar y planear;
          ahí ocultamos el flotante para que no tape las acciones de cada pendiente. */}
        {canQuickAdd &&
          location.pathname !== '/transacciones' &&
          location.pathname !== '/asistente' && (
            <Tooltip content="Registrar, mover dinero o planear un movimiento" align="end">
              <button
                onClick={() => setQuickActionsOpen(true)}
                aria-label="Agregar"
                aria-haspopup="dialog"
                className="tap-target fixed right-5 bottom-[calc(5.9rem+env(safe-area-inset-bottom))] z-20 flex h-[58px] w-[58px] items-center justify-center rounded-[20px] bg-slate-900 text-3xl font-light leading-none text-white shadow-[0_12px_26px_-8px_rgba(15,23,42,.6)] transition hover:bg-slate-800 active:scale-95 lg:right-8 lg:bottom-8 lg:rounded-xl"
              >
                +
              </button>
            </Tooltip>
          )}

        <Modal
          open={quickActionsOpen}
          title="¿Qué quieres hacer?"
          onClose={() => setQuickActionsOpen(false)}
        >
          <div className="space-y-2">
            <p className="mb-3 text-sm leading-5 text-slate-600">
              Elige el tipo de registro. Así tus saldos, presupuesto y tarjeta se calculan
              correctamente.
            </p>
            <QuickAction
              icon="wallet"
              title="Registrar movimiento"
              description="Dinero que entró o salió de una cuenta."
              onClick={() => openQuickFlow('transaction')}
            />
            <QuickAction
              icon="card"
              title="Registrar cargo de tarjeta"
              description="Una compra con crédito; se registra aparte de su pago."
              onClick={openCardCharge}
            />
            <QuickAction
              icon="swap"
              title="Mover dinero"
              description="Pasa dinero entre cuentas o apartados propios."
              onClick={() => openQuickFlow('transfer')}
            />
            <QuickAction
              icon="calendar"
              title="Planear algo"
              description="Un ingreso, gasto o ahorro que aún no ocurre."
              onClick={() => openQuickFlow('planned')}
            />
            <QuickAction
              icon="chart"
              title="Ver pendientes"
              description="Revisa fijos y movimientos planeados por confirmar."
              onClick={openPending}
            />
          </div>
        </Modal>

        <Modal
          open={quickFlow === 'transaction'}
          title="Nuevo movimiento"
          onClose={() => setQuickFlow(null)}
        >
          <QuickAddTransactionForm onDone={() => setQuickFlow(null)} />
        </Modal>
        <Modal
          open={quickFlow === 'transfer'}
          title="Nuevo traspaso"
          onClose={() => setQuickFlow(null)}
        >
          <TransferForm onDone={() => setQuickFlow(null)} />
        </Modal>
        <Modal
          open={quickFlow === 'planned'}
          title="Planear movimiento"
          onClose={() => setQuickFlow(null)}
        >
          <PlannedForm onDone={() => setQuickFlow(null)} />
        </Modal>
        <NotificationCenter />
      </div>
    </div>
  );
}

function QuickAction({
  icon,
  title,
  description,
  onClick,
}: {
  icon: NavItem['icon'];
  title: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex min-h-[64px] w-full items-center gap-3 rounded-xl border border-slate-200 px-3.5 py-3 text-left transition hover:border-slate-300 hover:bg-slate-50 focus-visible:border-slate-400"
    >
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-700 transition group-hover:bg-slate-900 group-hover:text-white">
        <NavIcon name={icon} className="h-5 w-5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-slate-800">{title}</span>
        <span className="mt-0.5 block text-xs leading-4 text-slate-500">{description}</span>
      </span>
      <svg
        className="h-4 w-4 shrink-0 text-slate-400 transition group-hover:translate-x-0.5 group-hover:text-slate-700"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="m9 18 6-6-6-6" />
      </svg>
    </button>
  );
}
